import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, Paper, Slider, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { formatISO, getTime } from 'date-fns';
import { clamp, last, max } from 'lodash';
import { WheelEventHandler, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { SoundPressureReading } from '@/api/historicalData';
import { sawtoothWave, sineWave, squareWave, triangleWave } from '@/assets';
import { PopoutButton } from '@/components/PopoutButton';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useNow } from '@/hooks/useNow';

type BandNodes = {
    bandNumber: number;
    frequency: number;
    oscillator: OscillatorNode;
    sourceGain: GainNode;
    eqGain: GainNode;
};

export type HydrophoneAudioPlayerHandle = { setPlaybackTimestamp: (timestamp: string | null) => void };

export const HydrophoneAudioPlayer = forwardRef<HydrophoneAudioPlayerHandle, { stream: SoundPressureReading[] }>(
    function HydrophoneAudioPlayer({ stream }, ref) {
        const [audioContext, setAudioContext] = useState<AudioContext>();
        const [startTimestamp, setStartTimestamp] = useState<number>();
        const { selectedSubsampleTimestamp, setSelectedSubsampleTimestamp } = useTimelineNavigationContext();
        const playing = audioContext?.state === 'running';
        const now = useNow(1, { enabled: playing });
        const [oscillatorType, setOscillatorType] = useState<OscillatorType>('sine');
        const [bandNodes, setBandNodes] = useState<Record<number, BandNodes>>();

        useImperativeHandle(
            ref,
            () => ({
                setPlaybackTimestamp(timestamp: string | null) {
                    if (!playing) {
                        return;
                    }

                    if (!timestamp) {
                        audioContext?.suspend();
                    } else {
                        setStartTimestamp(getTime(timestamp));
                    }
                },
            }),
            [audioContext, playing]
        );

        const maxSpl = useMemo(() => max(stream.map(({ bands }) => max(bands?.map(({ spl }) => spl))))!, [stream]);

        useEffect(() => {
            if (!stream?.length || !startTimestamp) {
                setAudioContext(undefined);
                return;
            }

            const ctx = new AudioContext();
            ctx.suspend();
            setAudioContext(ctx);

            const gainsByBand = Object.fromEntries(
                stream[0].bands.map(({ bandNumber, frequency }) => {
                    const oscillator = ctx.createOscillator();
                    oscillator.type = oscillatorType;
                    oscillator.frequency.value = frequency;
                    oscillator.start();

                    const sourceGain = ctx.createGain();
                    sourceGain.gain.value = 0;

                    const eqGain = ctx.createGain();
                    // if there was one before, inherit its value
                    eqGain.gain.value = bandNodes?.[bandNumber]?.eqGain.gain.value ?? 1;

                    oscillator.connect(sourceGain);
                    sourceGain.connect(eqGain);
                    eqGain.connect(ctx.destination);
                    return [bandNumber, { bandNumber, frequency, oscillator, sourceGain, eqGain }];
                })
            );

            setBandNodes(gainsByBand);

            const logGains: unknown[] = [];

            let lastTimestamp: number | null = null;
            stream.forEach(({ timestamp, bands }) => {
                const time = (getTime(timestamp) - startTimestamp) / 1000;
                if (time < 0) {
                    // this sample is before the play head
                    return;
                }
                if (lastTimestamp !== null && time > lastTimestamp + 1) {
                    // missing this second so we should treat it as silent
                    stream[0].bands.map(({ bandNumber }) => {
                        gainsByBand[bandNumber].sourceGain.gain.exponentialRampToValueAtTime(
                            0.00001,
                            lastTimestamp! + 1
                        );
                    });
                }
                lastTimestamp = time;
                bands.forEach(({ bandNumber, spl }) => {
                    const gainValue = Math.pow(10, (spl - maxSpl) / 10 + 0.5);
                    logGains.push({ bandNumber, spl, gainValue });
                    gainsByBand[bandNumber].sourceGain.gain.exponentialRampToValueAtTime(gainValue, time);
                });
            });

            ctx.resume();

            return () => {
                ctx.close();
            };
            // deliberately ignoring `bandNodes` because we just want to use the old value opportunistically if it exists,
            // but shouldn't react to it getting set which would cause an infinite loop
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [maxSpl, oscillatorType, startTimestamp, stream]);

        useEffect(() => {
            if (playing && startTimestamp) {
                const { contextTime } = audioContext.getOutputTimestamp();
                if (contextTime) {
                    const timestamp = contextTime * 1000 + startTimestamp;
                    if (timestamp > getTime(last(stream)!.timestamp)) {
                        audioContext?.suspend();
                        return;
                    }
                    const playbackTimestamp = formatISO(timestamp);
                    setSelectedSubsampleTimestamp(playbackTimestamp);
                }
            }
        }, [now, audioContext, playing, setSelectedSubsampleTimestamp, startTimestamp, stream]);

        const handlePlayPause = useCallback(() => {
            if (playing) {
                audioContext?.suspend();
            } else {
                const start = selectedSubsampleTimestamp ?? stream[0]?.timestamp;
                setStartTimestamp(getTime(start));
                audioContext?.resume();
            }
        }, [audioContext, playing, selectedSubsampleTimestamp, stream]);

        return (
            <Stack direction="row">
                <ToggleButton onChange={handlePlayPause} selected={playing} value="play" sx={{ flexGrow: 1 }}>
                    <PlayArrowIcon />
                </ToggleButton>
                {bandNodes && (
                    <PopoutButton
                        buttonContent={<TuneIcon sx={{ transform: 'rotate(90deg)' }} />}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <Paper
                            sx={{
                                backgroundColor: 'white',
                                p: 1,
                            }}
                        >
                            <Stack direction="row" sx={{ alignItems: 'center' }}>
                                <ToggleButtonGroup
                                    orientation="vertical"
                                    value={oscillatorType}
                                    exclusive
                                    onChange={(_e, value) => setOscillatorType(value)}
                                    size="small"
                                >
                                    <ToggleButton value="sine">
                                        <Box component="img" style={{ height: '20px' }} src={sineWave} alt="Sine" />
                                    </ToggleButton>
                                    <ToggleButton value="square">
                                        <Box component="img" style={{ height: '20px' }} src={squareWave} alt="Square" />
                                    </ToggleButton>
                                    <ToggleButton value="triangle">
                                        <Box
                                            component="img"
                                            style={{ height: '20px' }}
                                            src={triangleWave}
                                            alt="Triangle"
                                        />
                                    </ToggleButton>
                                    <ToggleButton value="sawtooth">
                                        <Box
                                            component="img"
                                            style={{ height: '20px' }}
                                            src={sawtoothWave}
                                            alt="Sawtooth"
                                        />
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                {(Object.values(bandNodes) || stream[0].bands).map((bandNodes) => (
                                    <EqBandSlider key={bandNodes.bandNumber} {...bandNodes} />
                                ))}
                            </Stack>
                        </Paper>
                    </PopoutButton>
                )}
            </Stack>
        );
    }
);

const EqBandSlider = ({
    bandNumber,
    frequency,
    eqGain,
}: {
    bandNumber: number;
    frequency: number;
    eqGain?: GainNode;
}) => {
    const [value, setValue] = useState(((eqGain?.gain.value ?? 1) - 1) * 10);

    const handleWheel: WheelEventHandler = useCallback((event) => {
        setValue((oldValue) => clamp(oldValue - event.deltaY / 8, -10, 10));
    }, []);

    useEffect(() => {
        if (!eqGain) return;

        eqGain.gain.value = 1 + value / 10;
    }, [value, eqGain]);

    return (
        <Stack key={bandNumber}>
            <Stack alignItems="center" height={200}>
                <Typography fontSize="0.8em">{bandNumber}</Typography>
                <Slider
                    sx={{ flexGrow: 1, px: '10px' }}
                    size="small"
                    orientation="vertical"
                    min={-10}
                    max={10}
                    value={value}
                    onChange={(_e, newValue) => setValue(newValue)}
                    onWheel={handleWheel}
                    marks={[
                        {
                            value: 0,
                        },
                    ]}
                />
                <Typography fontSize="0.5em">{frequency >= 1000 ? `${frequency / 1000}k` : frequency}</Typography>
            </Stack>
        </Stack>
    );
};
